'use client';

import { c as _c } from "react/compiler-runtime";
import { useIsMounted } from '@repo/hooks';
import { Button, Circle, cn, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@repo/ui';
import { abbreviateAddress } from '@repo/utils';
import { toast } from 'sonner';
import { useConnect } from 'wagmi';
import { isE2E_Next } from '../configs/process';
import { EXPLICIT_INJECTED_CONNECTOR_TYPE } from '../explicit-injected-provider';
import { useAppKitSSR } from '../hooks/useAppKitSSR';
import { useChains } from '../hooks/useChains';
import { useUserAccount } from '../providers/UserAccountProvider';
import { isSameChain } from '../utils/chain.utils';
import { AddressAvatar } from './AddressAvatar';
import { ChainIcon } from './ChainIcon';
const sizeConfig = {
  sm: {
    chainButton: 'iconSm',
    mainButton: 'sm',
    chainIcon: 'size-3',
    avatar: 16
  },
  md: {
    chainButton: 'icon',
    mainButton: 'default',
    chainIcon: 'size-4',
    avatar: 16
  },
  lg: {
    chainButton: 'iconLg',
    mainButton: 'lg',
    chainIcon: 'size-5',
    avatar: 20
  }
} as const;
type ConnectWalletSize = keyof typeof sizeConfig;
function MockWalletConnect() {
  const $ = _c(8);
  const {
    connectors,
    connect
  } = useConnect();
  const {
    isConnected,
    userAddress,
    chain
  } = useUserAccount();
  const mockConnector = connectors[0];
  if (!mockConnector) {
    return null;
  }
  if (isConnected && userAddress) {
    let t0;
    if ($[0] !== userAddress) {
      t0 = abbreviateAddress(userAddress, 4);
      $[0] = userAddress;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    const t1 = chain?.name;
    let t2;
    if ($[2] !== t0 || $[3] !== t1) {
      t2 = <Button>Connected ({t0}) on {t1}</Button>;
      $[2] = t0;
      $[3] = t1;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  let t0;
  if ($[5] !== connect || $[6] !== mockConnector) {
    t0 = <Button onClick={() => connect({
      connector: mockConnector
    })}>Mock connect</Button>;
    $[5] = connect;
    $[6] = mockConnector;
    $[7] = t0;
  } else {
    t0 = $[7];
  }
  return t0;
}

/**
 * Wallet connection control with chain switching and address display.
 * Uses {@link useAppKitSSR} so it can render safely in server-rendered trees
 * where `useAppKit` would throw before AppKit is initialized.
 */
export function ConnectWallet(t0) {
  const $ = _c(38);
  const {
    hideChainSelector,
    size: t1,
    connectedVariant: t2,
    connectedSuffix,
    ...props
  } = t0;
  const size = t1 === undefined ? "sm" : t1;
  const connectedVariant = t2 === undefined ? "secondary" : t2;
  const config = sizeConfig[size];
  const isMounted = useIsMounted();
  const {
    connectors,
    connect
  } = useConnect();
  const {
    isConnected,
    chain: userChain,
    userAddress,
    ensName,
    switchNetwork
  } = useUserAccount();
  const {
    open
  } = useAppKitSSR();
  const chains = useChains();
  const isSettled = isMounted && isConnected;
  let t3;
  if ($[0] !== connectors) {
    t3 = connectors.find(_temp);
    $[0] = connectors;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const explicitConnector = t3;
  let t4;
  if ($[2] !== connect || $[3] !== explicitConnector || $[4] !== open) {
    t4 = () => {
      if (explicitConnector) {
        connect({
          connector: explicitConnector
        }, {
          onError: _temp2
        });
        return;
      }
      open();
    };
    $[2] = connect;
    $[3] = explicitConnector;
    $[4] = open;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const connectWallet = t4;
  if (isE2E_Next && !explicitConnector) {
    let t5;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <MockWalletConnect />;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    return t5;
  }
  if (!userAddress) {
    return <Button type="button" color="primary" size={config.mainButton} onClick={connectWallet} data-testid="connect-wallet-button" {...props}>Connect</Button>;
  }
  const connectedAddress = userAddress;
  const t5 = "flex gap-2";
  let t6;
  if ($[7] !== chains || $[8] !== config.chainButton || $[9] !== config.chainIcon || $[10] !== hideChainSelector || $[11] !== switchNetwork || $[12] !== userChain?.id) {
    t6 = !hideChainSelector && <DropdownMenu><DropdownMenuTrigger asChild={true}><Button type="button" variant="secondary" size={config.chainButton} aria-label="Switch Chain Button"><ChainIcon chainId={userChain?.id ?? 1} className={config.chainIcon} /></Button></DropdownMenuTrigger><DropdownMenuContent className="w-56" side="bottom" align="end">{chains.map(chain => <DropdownMenuItem key={chain.id} onClick={() => switchNetwork(chain)} className="flex cursor-pointer items-center gap-3"><div className="flex w-full items-center gap-2"><ChainIcon chainId={chain.id} className="size-4" /><span className="flex-1 text-left">{chain.name}</span>{isSameChain(userChain?.id ?? 1, chain.id) && <Circle size="sm" color="valid" />}</div></DropdownMenuItem>)}</DropdownMenuContent></DropdownMenu>;
    $[7] = chains;
    $[8] = config.chainButton;
    $[9] = config.chainIcon;
    $[10] = hideChainSelector;
    $[11] = switchNetwork;
    $[12] = userChain?.id;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const T0 = Button;
  const t7 = "button";
  const t8 = config.mainButton;
  let t9;
  if ($[14] !== open) {
    t9 = () => open();
    $[14] = open;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const t10 = !isSettled;
  const t11 = "connected-wallet-button";
  const t12 = cn(!isSettled && "animate-pulse", props.className);
  let t13;
  if ($[16] !== config.avatar || $[17] !== connectedAddress) {
    t13 = <AddressAvatar address={connectedAddress} size={config.avatar} />;
    $[16] = config.avatar;
    $[17] = connectedAddress;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== connectedAddress || $[20] !== ensName) {
    t14 = ensName ?? abbreviateAddress(connectedAddress);
    $[19] = connectedAddress;
    $[20] = ensName;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== t14) {
    t15 = <span className="truncate">{t14}</span>;
    $[22] = t14;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== T0 || $[25] !== config.mainButton || $[26] !== connectedSuffix || $[27] !== connectedVariant || $[28] !== props || $[29] !== t10 || $[30] !== t12 || $[31] !== t13 || $[32] !== t15 || $[33] !== t9) {
    t16 = <T0 type={t7} size={t8} variant={connectedVariant} onClick={t9} disabled={t10} data-testid={t11} {...props} className={t12}>{t13}{t15}{connectedSuffix}</T0>;
    $[24] = T0;
    $[25] = config.mainButton;
    $[26] = connectedSuffix;
    $[27] = connectedVariant;
    $[28] = props;
    $[29] = t10;
    $[30] = t12;
    $[31] = t13;
    $[32] = t15;
    $[33] = t9;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t16 || $[36] !== t6) {
    t17 = <div className={t5}>{t6}{t16}</div>;
    $[35] = t16;
    $[36] = t6;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  return t17;
}
function _temp2() {
  return toast.error("Wallet connection failed. Reload and try again.");
}
function _temp(connector) {
  return connector.type === EXPLICIT_INJECTED_CONNECTOR_TYPE;
}